/**
 * H265 播放器统一接口
 * 自动选择硬解码或软解码，对外提供统一的 API
 */

import mpegts from "mpegts.js";
import { H265SoftDecoder } from "./h265SoftDecoder";

/* eslint-disable no-console */

export interface PlayerConfig {
    type: "flv";
    isLive: boolean;
    hasAudio: boolean;
    hasVideo: boolean;
    url: string;
}

export interface PlayerOptions {
    enableWorker?: boolean;
    enableStashBuffer?: boolean;
    autoCleanupSourceBuffer?: boolean;
    lazyLoadMaxDuration?: number;
    deferLoadAfterSourceOpen?: boolean;
}

export enum PlayerEvents {
    ERROR = "error",
    LOADING_COMPLETE = "loading_complete",
    RECOVERED_EARLY_EOF = "recovered_early_eof",
    MEDIA_INFO = "media_info",
    METADATA_ARRIVED = "metadata_arrived",
    SCRIPTDATA_ARRIVED = "scriptdata_arrived",
    STATISTICS_INFO = "statistics_info"
}

export enum PlayerErrorTypes {
    NETWORK_ERROR = "NetworkError",
    MEDIA_ERROR = "MediaError",
    OTHER_ERROR = "OtherError"
}

/**
 * 统一的播放器接口
 * 自动选择硬解码（mpegts.js）或软解码（libde265）
 */
export class H265Player {
    private config: PlayerConfig;
    private options: PlayerOptions;
    private videoElement: HTMLVideoElement;
    private hardwarePlayer: mpegts.Player | null = null;
    private softwareDecoder: H265SoftDecoder | null = null;
    private useSoftDecode: boolean = false;
    private eventHandlers: Map<string, Set<Function>> = new Map();
    private mediaErrorCount: number = 0;
    private readonly MAX_MEDIA_ERROR_COUNT = 3;
    private canvasElement: HTMLCanvasElement | null = null;
    /** 软解时保留的 video 原始 play/pause，用于恢复 */
    private originalVideoPlay: (() => Promise<void>) | null = null;
    private originalVideoPause: (() => void) | null = null;
    /** 软解时保留的 video 原始 paused 描述符，用于恢复 */
    private originalPausedDescriptor: PropertyDescriptor | null = null;

    constructor(config: PlayerConfig, options: PlayerOptions = {}) {
        this.config = config;
        this.options = options;
        this.videoElement = document.createElement("video");
    }

    /**
     * 检查是否支持硬解码
     */
    private checkHardwareSupport(): boolean {
        return mpegts.getFeatureList().msePlayback && MediaSource.isTypeSupported("video/mp4; codecs=hvc1.1.1.L123.B0");
    }

    /**
     * 初始化硬解码播放器
     */
    private initHardwarePlayer(): void {
        if (this.hardwarePlayer) {
            this.hardwarePlayer.unload();
            this.hardwarePlayer.destroy();
        }

        this.hardwarePlayer = mpegts.createPlayer(
            {
                type: this.config.type,
                isLive: this.config.isLive,
                hasAudio: this.config.hasAudio,
                hasVideo: this.config.hasVideo,
                url: this.config.url
            },
            {
                enableWorker: this.options.enableWorker ?? true,
                enableStashBuffer: this.options.enableStashBuffer ?? true,
                autoCleanupSourceBuffer: this.options.autoCleanupSourceBuffer ?? true,
                lazyLoadMaxDuration: this.options.lazyLoadMaxDuration ?? 3 * 60,
                deferLoadAfterSourceOpen: this.options.deferLoadAfterSourceOpen ?? true
            }
        );

        this.hardwarePlayer.attachMediaElement(this.videoElement);

        // 转发硬解码播放器的错误事件
        this.hardwarePlayer.on(mpegts.Events.ERROR, (errorType: string, errorDetail: any) => {
            console.log("[H265Player] 硬解码错误:", errorType, errorDetail);

            if (errorType === mpegts.ErrorTypes.MEDIA_ERROR) {
                this.mediaErrorCount++;
                console.log(`[H265Player] 媒体错误计数: ${this.mediaErrorCount}/${this.MAX_MEDIA_ERROR_COUNT}`);

                // 如果错误次数超过阈值，切换到软解码
                if (this.mediaErrorCount >= this.MAX_MEDIA_ERROR_COUNT && H265SoftDecoder.isAvailable()) {
                    console.log("[H265Player] 切换到软解码模式");
                    this.switchToSoftDecode();
                    return;
                }
            }

            // 触发错误事件
            this.emit(PlayerEvents.ERROR, errorType, errorDetail);
        });
    }

    /**
     * 初始化软解码播放器（保留 video 供 Plyr 控制，canvas 叠在上面显示画面）
     */
    private initSoftwareDecoder(): void {
        if (!this.canvasElement) {
            this.canvasElement = document.createElement("canvas");
            this.canvasElement.className = this.videoElement.className;
            this.canvasElement.style.cssText = this.videoElement.style.cssText;

            const parent = this.videoElement.parentNode;
            if (parent) {
                // 不替换 video：将 canvas 插入到 video 后面，叠在同一区域
                parent.appendChild(this.canvasElement);
                // canvas 由软解码器按视频比例设置尺寸和位置，保持原始比例不拉伸
                this.canvasElement.style.position = "absolute";
                this.canvasElement.style.left = "0";
                this.canvasElement.style.top = "0";
                this.canvasElement.style.maxWidth = "100%";
                this.canvasElement.style.maxHeight = "100%";
                this.canvasElement.style.pointerEvents = "none"; // 点击穿透到 Plyr 控制
                // video 隐藏但保留，Plyr 仍可调用其 play/pause
                this.videoElement.style.position = "absolute";
                this.videoElement.style.left = "0";
                this.videoElement.style.top = "0";
                this.videoElement.style.width = "100%";
                this.videoElement.style.height = "100%";
                this.videoElement.style.opacity = "0";
                this.videoElement.style.pointerEvents = "none";
                this.videoElement.style.zIndex = "0";
                this.canvasElement.style.zIndex = "1";
            }
        }

        // 代理 video.play / video.pause，使 Plyr 的播放/暂停控制软解码器
        this.originalVideoPlay = this.videoElement.play.bind(this.videoElement);
        this.originalVideoPause = this.videoElement.pause.bind(this.videoElement);
        this.videoElement.play = () => this.play() as Promise<void>;
        this.videoElement.pause = () => this.pause();

        // 覆盖 video.paused，让 Plyr 读取到正确的播放状态（软解时 video 本身未播，paused 会一直是 true）
        const proto = HTMLVideoElement.prototype;
        this.originalPausedDescriptor = Object.getOwnPropertyDescriptor(proto, "paused") ?? null;
        const videoEl = this.videoElement;
        Object.defineProperty(this.videoElement, "paused", {
            get: () => {
                if (this.useSoftDecode && this.softwareDecoder) {
                    return this.softwareDecoder.isPaused() || !this.softwareDecoder.isRunning();
                }
                return this.originalPausedDescriptor?.get?.call(videoEl) ?? true;
            },
            configurable: true,
            enumerable: true
        });

        this.softwareDecoder = new H265SoftDecoder({
            canvas: this.canvasElement,
            isLive: this.config.isLive,
            onStatusChange: status => {
                console.log("[H265Player] 软解码状态:", status);
            },
            onError: error => {
                console.error("[H265Player] 软解码错误:", error);
                this.emit(PlayerEvents.ERROR, PlayerErrorTypes.MEDIA_ERROR, error);
            },
            onPlaying: () => {
                this.dispatchVideoPlayState(true);
            },
            onPaused: () => {
                this.dispatchVideoPlayState(false);
            }
        });
    }

    /**
     * 派发 video 的 play/pause 状态，让 Plyr 同步（延迟一帧确保 Plyr 已就绪）
     */
    private dispatchVideoPlayState(playing: boolean): void {
        const ev = playing ? "play" : "pause";
        const ev2 = playing ? "playing" : "pause";
        const el = this.videoElement;
        const fire = () => {
            el.dispatchEvent(new Event(ev, { bubbles: true }));
            if (playing) el.dispatchEvent(new Event(ev2, { bubbles: true }));
        };
        if (typeof requestAnimationFrame !== "undefined") {
            requestAnimationFrame(() => fire());
        } else {
            setTimeout(fire, 0);
        }
    }

    /**
     * 切换到软解码模式
     */
    private async switchToSoftDecode(): Promise<void> {
        if (this.useSoftDecode) return;

        this.useSoftDecode = true;

        // 停止硬解码播放器
        if (this.hardwarePlayer) {
            this.hardwarePlayer.unload();
            this.hardwarePlayer.destroy();
            this.hardwarePlayer = null;
        }

        // 初始化并启动软解码播放器
        this.initSoftwareDecoder();
        if (this.softwareDecoder) {
            try {
                await this.softwareDecoder.play(this.config.url);
            } catch (error) {
                console.error("[H265Player] 软解码启动失败:", error);
                this.emit(PlayerEvents.ERROR, PlayerErrorTypes.OTHER_ERROR, error);
            }
        }
    }

    /**
     * 绑定到视频元素
     */
    attachMediaElement(videoElement: HTMLVideoElement): void {
        this.videoElement = videoElement;
    }

    /**
     * 加载视频
     */
    load(): void {
        if (this.useSoftDecode) {
            // 软解码模式下，load 在 play 时自动执行
            return;
        }

        if (!this.checkHardwareSupport()) {
            // 不支持硬解码，直接使用软解码
            if (H265SoftDecoder.isAvailable()) {
                console.log("[H265Player] 浏览器不支持硬解码，使用软解码模式");
                this.useSoftDecode = true;
                this.initSoftwareDecoder();
            } else {
                console.error("[H265Player] 浏览器不支持硬解码，且软解码库未加载");
                this.emit(
                    PlayerEvents.ERROR,
                    PlayerErrorTypes.OTHER_ERROR,
                    new Error("浏览器不支持硬解码，且软解码库未加载")
                );
            }
            return;
        }

        this.initHardwarePlayer();
        if (this.hardwarePlayer) {
            this.hardwarePlayer.load();
        }
    }

    /**
     * 播放视频
     */
    async play(): Promise<void> {
        if (this.useSoftDecode) {
            if (!this.softwareDecoder) {
                this.initSoftwareDecoder();
            }
            if (this.softwareDecoder) {
                if (this.softwareDecoder.isPaused()) {
                    this.softwareDecoder.resume();
                } else if (!this.softwareDecoder.isRunning()) {
                    await this.softwareDecoder.play(this.config.url);
                } else {
                    // 已在播放时也派发 play，同步 Plyr 状态（避免 UI 显示停止）
                    this.videoElement.dispatchEvent(new Event("play"));
                }
            }
        } else {
            if (this.hardwarePlayer) {
                await this.hardwarePlayer.play();
            }
        }
    }

    /**
     * 暂停播放
     */
    pause(): void {
        if (this.useSoftDecode) {
            this.softwareDecoder?.pause();
        } else {
            if (this.hardwarePlayer) {
                this.hardwarePlayer.pause();
            }
        }
    }

    /**
     * 卸载播放器
     */
    unload(): void {
        if (this.useSoftDecode) {
            this.softwareDecoder?.stop();
        } else {
            if (this.hardwarePlayer) {
                this.hardwarePlayer.unload();
            }
        }
    }

    /**
     * 销毁播放器
     */
    destroy(): void {
        if (this.useSoftDecode) {
            this.softwareDecoder?.destroy();
            this.softwareDecoder = null;

            // 恢复 video 的 play/pause 和 paused
            if (this.originalVideoPlay) this.videoElement.play = this.originalVideoPlay;
            if (this.originalVideoPause) this.videoElement.pause = this.originalVideoPause;
            this.originalVideoPlay = null;
            this.originalVideoPause = null;
            if (this.originalPausedDescriptor) {
                Object.defineProperty(this.videoElement, "paused", this.originalPausedDescriptor);
                this.originalPausedDescriptor = null;
            }

            // 移除 canvas，恢复 video 样式
            if (this.canvasElement?.parentNode) {
                this.canvasElement.parentNode.removeChild(this.canvasElement);
                this.canvasElement = null;
            }
            this.videoElement.style.position = "";
            this.videoElement.style.left = "";
            this.videoElement.style.top = "";
            this.videoElement.style.width = "";
            this.videoElement.style.height = "";
            this.videoElement.style.opacity = "";
            this.videoElement.style.pointerEvents = "";
            this.videoElement.style.zIndex = "";
        } else {
            if (this.hardwarePlayer) {
                this.hardwarePlayer.destroy();
                this.hardwarePlayer = null;
            }
        }

        this.eventHandlers.clear();
        this.mediaErrorCount = 0;
    }

    /**
     * 监听事件
     */
    on(event: string, handler: Function): void {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, new Set());
        }
        this.eventHandlers.get(event)!.add(handler);
    }

    /**
     * 移除事件监听
     */
    off(event: string, handler: Function): void {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.delete(handler);
        }
    }

    /**
     * 触发事件
     */
    private emit(event: string, ...args: any[]): void {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(...args);
                } catch (error) {
                    console.error(`[H265Player] 事件处理器错误 (${event}):`, error);
                }
            });
        }
    }

    /**
     * 获取当前是否使用软解码
     */
    isUsingSoftDecode(): boolean {
        return this.useSoftDecode;
    }
}

/**
 * 创建播放器实例
 */
export function createPlayer(config: PlayerConfig, options?: PlayerOptions): H265Player {
    return new H265Player(config, options);
}

/**
 * 获取功能支持列表（兼容 mpegts.js API）
 */
export function getFeatureList() {
    return mpegts.getFeatureList();
}

/**
 * 导出事件和错误类型（兼容 mpegts.js API）
 */
export const Events = PlayerEvents;
export const ErrorTypes = PlayerErrorTypes;
